"use client";

import { liteClient as algoliasearch } from "algoliasearch/lite";
import { useTranslations } from "next-intl";
import NextImage, { type ImageProps as NextImageProps } from "next/image";
import { type LinkProps as NextLinkProps } from "next/link";

import { useEffect, useState } from "react";
import { InstantSearch } from "react-instantsearch";

import { getCartUrl } from "@/app/actions/get-cart-url";
import { Header, type HeaderProps } from "@/composite/Header";
import { MyShows, type Show } from "@/app/components/shared/MyShows";

import { ConsultantConsultant } from "@/app/components/blocks/contact-consultant/client";
import {
	ConsultantState,
	UserState,
	type UserType,
	useCartStore,
	useConsultantStore,
	usePartyStore,
	useSessionStore,
	useUserStore
} from "@/app/store";
import { resolveImageProps } from "@/app/utilities/images";
import {
	ContactConsultantReference,
	HeaderReference,
	ImageAsset,
	LinkField
} from "@/types";
import { determineLinks } from "./determine-links";
import {
	fetchPartyByHost,
	getHostDashboardUrlAction
} from "@/app/actions/party";

interface MainNavigationDropdown {
	title: string;
	headline_url: LinkField;
	mobile_desktop_visibility: "Desktop Only" | "Mobile Only" | "Both";
	reference: MainNavigationLink[];
	thumbnail_image: ImageAsset;
}

interface MainNavigationLink {
	title: string;
	parent_link: LinkField;
	mobile_desktop_visibility: "Desktop Only" | "Mobile Only" | "Both";
	image_thumbnail: ImageAsset;
}

type MainNavigationItem = MainNavigationDropdown | MainNavigationLink;

export interface UserInfo {
	userType: UserType;
	isPartyHost: boolean;
	hostDashboardUrl?: string;
}

/**
 * Type-guard to determine if an nav item is a dropdown
 */
function isNavDropdown(
	item: MainNavigationItem
): item is MainNavigationDropdown {
	return !!(item as MainNavigationDropdown).reference;
}

/**
 * Determines whether a nav dropdown should be rendered as a category section
 * on mobile
 */
function isMobileCategorySection(section: MainNavigationDropdown): boolean {
	return section.reference.every((link) => !!link.image_thumbnail.image);
}

function resolvePersonalWebsiteHref(
	consultantState: ConsultantState,
	fallbackHref: string
): string {
	if (consultantState.status !== "connected") return fallbackHref;

	const sanitized = consultantState.pwsUrl?.trim();

	if (!sanitized) return fallbackHref;
	if (sanitized.startsWith("/")) return sanitized;

	return `/pws/${sanitized}`;
}

export interface HeaderBlockProps extends Omit<
	HeaderReference,
	"main_navigation_parents"
> {
	countrySwitcherUrl?: string;
	searchData: {
		appId?: string;
		apiKey?: string;
		indexName?: string;
	};
	captchaSiteKey: string;
	main_navigation_parents: MainNavigationItem[];
}

/**
 * Return account links, based on user state.
 */
function getAccountLinks(
	userType: UserType,
	props: HeaderBlockProps,
	consultantState: ConsultantState
): HeaderProps<NextLinkProps, NextImageProps>["utilityLinks"]["accountLinks"] {
	const unauthAccountLinks = [
		{
			children: props.account_links.sign_in.title,
			href: props.account_links.sign_in.href
		},
		{
			children: props.account_links.create_account.title,
			href: props.account_links.create_account.href
		},
		{
			children: props.account_links.my_orders.title,
			href: props.account_links.my_orders.href
		},
		{
			children: props.account_links.registry.title,
			href: props.account_links.registry.href
		},
		{
			children: props.account_links.wishlist.title,
			href: props.account_links.wishlist.href
		}
	];

	if (userType === "consultant") {
		const personalWebsiteHref = resolvePersonalWebsiteHref(
			consultantState,
			props.account_links.view_my_personal_website.href
		);

		return [
			{
				children: props.account_links.consultant_corner.title,
				href: props.account_links.consultant_corner.href
			},
			{
				children: props.account_links.view_my_personal_website.title,
				href: personalWebsiteHref
			},
			{
				children: props.account_links.manage_personal_website.title,
				href: props.account_links.manage_personal_website.href
			},
			{
				children: props.account_links.my_account.title,
				href: props.account_links.my_account.href
			},
			{
				children: props.account_links.my_orders.title,
				href: props.account_links.my_orders.href
			},
			{
				children: props.account_links.sign_out.title,
				href: props.account_links.sign_out.href
			}
		];
	}

	const authAccountLinks = [
		{
			children: props.account_links.my_account.title,
			href: props.account_links.my_account.href
		},
		{
			children: props.account_links.my_orders.title,
			href: props.account_links.my_orders.href
		},
		{
			children: props.account_links.registry.title,
			href: props.account_links.registry.href
		},
		{
			children: props.account_links.wishlist.title,
			href: props.account_links.wishlist.href
		},
		{
			children: props.account_links.sign_out.title,
			href: props.account_links.sign_out.href
		}
	];

	return userType === "anonymous" ? unauthAccountLinks : authAccountLinks;
}

/**
 * Return consultant image, based on user state.
 * TODO: Add consultant-specific data once it's implemented.
 */
function getConsultantImage(
	consultantState: ConsultantState,
	_props: HeaderBlockProps
): HeaderProps<NextLinkProps, NextImageProps>["consultantImage"] | undefined {
	return consultantState.status === "connected"
		? consultantState.image !== ""
			? {
					src: `https://www.pamperedchef.com/${consultantState.image}`,
					alt: `${consultantState.firstName} ${consultantState.lastName}`
				}
			: {
					src: "https://www.pamperedchef.com/iceberg/pws/profilepic/default-profilepic.jpg",
					alt: "placeholder"
				}
		: undefined;
}

/**
 * Return find links, based on user state.
 * TODO: Add user-specific data once it's implemented.
 */
function getMobileNav(
	userType: UserType,
	props: HeaderBlockProps,
	consultantState: ConsultantState,
	userState: UserState
): HeaderProps<NextLinkProps, NextImageProps>["mobileNav"]["sections"] {
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const t = useTranslations("header");

	const unauthMobileAccountLinks: HeaderProps<
		NextLinkProps,
		NextImageProps
	>["mobileNav"]["sections"] = [
		{
			type: "link",
			items: [
				{
					children: props.account_links.sign_in.title,
					href: props.account_links.sign_in.href
				},
				{
					children: props.account_links.create_account.title,
					href: props.account_links.create_account.href
				},
				{
					children: props.account_links.my_orders.title,
					href: props.account_links.my_orders.href
				}
			]
		}
	];
	const mobileNavItems: HeaderProps<
		NextLinkProps,
		NextImageProps
	>["mobileNav"]["sections"] = props.main_navigation_parents
		.filter(
			(navItem) => navItem.mobile_desktop_visibility !== "Desktop Only"
		)
		.map((navItem) =>
			isNavDropdown(navItem)
				? isMobileCategorySection(navItem)
					? {
							type: "category",
							heading: navItem.headline_url.title
								? {
										tag: "h2",
										children: navItem.headline_url.title
									}
								: undefined,
							items: navItem.reference.map((item) => ({
								image: {
									src: item.image_thumbnail.image,
									alt: item.image_thumbnail.image_alt_text
								},
								link: {
									children: item.parent_link.title,
									href: item.parent_link.href
								}
							}))
						}
					: {
							type: "link",
							heading: navItem.headline_url.title
								? {
										tag: "h2",
										children: navItem.headline_url.title
									}
								: undefined,
							items: navItem.reference.map((item) => ({
								children: item.parent_link.title,
								href: item.parent_link.href
							}))
						}
				: {
						type: "link",
						items: [
							{
								children: navItem.parent_link.title,
								href: navItem.parent_link.href
							}
						]
					}
		);
	const mobileLocaleLinks: HeaderProps<
		NextLinkProps,
		NextImageProps
	>["mobileNav"]["sections"] = [
		{
			type: "link",
			heading: { tag: "h2", children: t("mobileMenuLocaleHeading") },
			items: [
				{
					children: t("mobileMenuLocaleCurrent", {
						country: props.locales.current_country
					}),
					href: "#"
				},
				...props.locales.other_locales.map((locale) => ({
					children: locale.locale_link.title,
					href: props.countrySwitcherUrl ?? locale.locale_link.href
				}))
			]
		}
	];

	switch (userType) {
		case "customer":
			return [
				{
					type: "container",
					subsections: [
						{
							type: "text",
							heading: {
								tag: "h2",
								children: `Hi, ${userState.type === "customer" && userState.firstName ? userState.firstName : "User"}`
							}
						},
						{
							type: "link",
							items: [
								{
									children:
										props.account_links.my_account.title,
									href: props.account_links.my_account.href
								},
								{
									children:
										props.account_links.my_orders.title,
									href: props.account_links.my_orders.href
								}
							]
						},
						{
							type: "link",
							items: [
								{
									children:
										props.account_links.sign_out.title,
									href: props.account_links.sign_out.href
								}
							]
						}
					]
				},
				...mobileNavItems,
				...mobileLocaleLinks
			];
		case "consultant":
			return [
				{
					type: "container",
					subsections: [
						{
							type: "text",
							heading: {
								tag: "h2",
								children: `Hi, ${userState.type === "consultant" && userState.firstName ? userState.firstName : "Consultant"}`
							}
						},
						{
							type: "link",
							items: [
								{
									children:
										props.account_links.consultant_corner
											.title,
									href: props.account_links.consultant_corner
										.href
								},
								{
									children:
										props.account_links
											.view_my_personal_website.title,
									href: resolvePersonalWebsiteHref(
										consultantState,
										props.account_links
											.view_my_personal_website.href
									)
								},
								{
									children:
										props.account_links
											.manage_personal_website.title,
									href: props.account_links
										.manage_personal_website.href
								},
								{
									children:
										props.account_links.my_account.title,
									href: props.account_links.my_account.href
								},
								{
									children:
										props.account_links.my_orders.title,
									href: props.account_links.my_orders.href
								}
							]
						},
						{
							type: "link",
							items: [
								{
									children:
										props.account_links.sign_out.title,
									href: props.account_links.sign_out.href
								}
							]
						},
						...mobileNavItems,
						...mobileLocaleLinks
					]
				}
			];
		case "anonymous":
		default:
			return [
				...unauthMobileAccountLinks,
				...mobileNavItems,
				...mobileLocaleLinks
			];
	}
}

/**
 * Header component
 */
export function HeaderBlock(props: HeaderBlockProps): JSX.Element {
	const sessionState = useSessionStore.getState().getSessionState();

	const cartItemNumber = useCartStore((state) => state.getCartCount());
	const userType = useUserStore((state) => state.getUserType());
	const userState = useUserStore((state) => state.getUserState());
	const isPartyHost = useUserStore((state) => state.isPartyHost());
	const partyState = usePartyStore((state) => state.getPartyState());

	const consultantState = useConsultantStore((state) =>
		state.getConsultantState()
	);

	const [hostDashboardUrl, setHostDashboardUrl] = useState<
		string | undefined
	>();

	useEffect(() => {
		const fetchUrl = async () => {
			if (isPartyHost && partyState.status === "connected") {
				const url = await getHostDashboardUrlAction(partyState.partyId);
				console.log(">> url", url);
				if (url) {
					setHostDashboardUrl(url);
				}
			}
		};
		fetchUrl();
	}, [isPartyHost, partyState]);

	const userInfo: UserInfo = {
		userType,
		isPartyHost,
		hostDashboardUrl
	};
	const findLinks = determineLinks(userInfo, props);
	const knowledgeCenter =
		userType === "consultant"
			? {
					children: props.account_links.knowledge_center.title,
					href: props.account_links.knowledge_center.href
				}
			: undefined;
	const accountLinks = getAccountLinks(userType, props, consultantState);
	const consultantImage = getConsultantImage(consultantState, props);
	const contactConsultantInfo: ContactConsultantReference | undefined =
		consultantState.status === "connected"
			? {
					consultant: {
						consultantNumber: consultantState.consultantNumber,
						name:
							consultantState.firstName +
							" " +
							consultantState.lastName,
						location:
							consultantState.address.city +
							", " +
							consultantState.address.state.toUpperCase(),
						image: consultantImage?.src
					},
					triggerElement: null,
					siteKey: props.captchaSiteKey,
					thankYouMessage: {
						title: "Message Sent to Your Consultant",
						description: `Thanks for reaching out. Your message was sent successfully, and ${consultantState.firstName} ${consultantState.lastName} will follow up with you soon.`
					}
				}
			: undefined;
	const mobileNav = getMobileNav(userType, props, consultantState, userState);
	const accountMenuLabel =
		userType === "consultant"
			? props.account_links.my_account.title
			: undefined;
	const [showContactConsultant, setShowContactConsultant] = useState(false);
	const [showMyShows, setShowMyShows] = useState(false);
	const [hostParties, setHostParties] = useState<Show[]>([]);
	const [isLoadingMyShows, setIsLoadingMyShows] = useState(false);
	const [hasFetchedMyShows, setHasFetchedMyShows] = useState(false);
	const onContactConsultantClick =
		consultantState.status === "connected"
			? () => setShowContactConsultant(true)
			: undefined;

	const onMyShowsClick = async () => {
		setShowMyShows(true);
		if (!hasFetchedMyShows && !isLoadingMyShows) {
			setIsLoadingMyShows(true);
			try {
				if (userState.type !== "anonymous" && sessionState) {
					const parties = await fetchPartyByHost(sessionState);
					setHostParties(parties);
					setHasFetchedMyShows(true);
				}
			} catch (error) {
				console.error("client:party-by-host-name", error);
			} finally {
				setIsLoadingMyShows(false);
			}
		}
	};

	useEffect(() => {
		setHasFetchedMyShows(false);
		setHostParties([]);
	}, [sessionState, userState]);

	const mainNavItems: HeaderProps<NextLinkProps, NextImageProps>["navItems"] =
		props.main_navigation_parents
			.filter(
				(navItem) => navItem.mobile_desktop_visibility !== "Mobile Only"
			)
			.map((navItem) =>
				isNavDropdown(navItem)
					? {
							type: "menu",
							label: navItem.headline_url.title,
							items: navItem.reference.map((navLink) => ({
								children: navLink.parent_link.title,
								href: navLink.parent_link.href
							}))
						}
					: {
							type: "link",
							link: {
								children: navItem.parent_link.title,
								href: navItem.parent_link.href
							}
						}
			);

	/**
	 * Redirects to the correct cart URL
	 */
	async function goToCart(): Promise<void> {
		if (!sessionState) return;
		const url = await getCartUrl(sessionState.sessionId);

		if (url !== undefined) {
			window.location.href = url;
		} else {
			// eslint-disable-next-line no-console
			console.error("Failed to land on cart.");
		}
	}

	useEffect(() => {
		useSessionStore.getState().setSessionState(sessionState);
	}, [sessionState]);

	return (
		<>
			<Header<NextLinkProps, NextImageProps>
				logo={{
					...resolveImageProps(),
					component: NextImage,
					src: props.site_logo.url,
					alt: "Pampered Chef",
					width: 250,
					height: 54
				}}
				logoLink={{
					href: props.logo_url.href
				}}
				utilityLinks={{
					findLinks: findLinks,
					knowledgeCenter: knowledgeCenter,
					accountLinks: accountLinks,
					accountMenuLabel,
					countryList: props.locales.other_locales.map((locale) => ({
						flagImage: {
							src: locale.locale_flag.url,
							alt: locale.local_name
						},
						label: `${locale.locale_link.title} (${locale.local_name.split("-")[0]})`,
						href:
							props.countrySwitcherUrl ?? locale.locale_link.href
					})),
					currentCountryImage: {
						src: props.locales.current_country_flag.url,
						alt: props.locales.current_country
					}
				}}
				consultantImage={consultantImage}
				contactConsultantInfo={contactConsultantInfo}
				navItems={mainNavItems}
				cart={{
					cta: { onPress: goToCart },
					itemNumber: cartItemNumber
				}}
				search={{
					inputProps: {
						placeholder: props.search.search_text
					},
					decorator: (element) => (
						<InstantSearch
							searchClient={algoliasearch(
								props.searchData.appId ?? "",
								props.searchData.apiKey ?? ""
							)}
							indexName={props.searchData.indexName}
						>
							{element}
						</InstantSearch>
					)
				}}
				mobileNav={{
					label: props.mobile_navigation_menu_localization,
					sections: mobileNav
				}}
				onContactConsultantClick={onContactConsultantClick}
				onMyShowsClick={onMyShowsClick}
			/>
			{showContactConsultant && contactConsultantInfo && (
				<div>
					<ConsultantConsultant
						onClose={() => setShowContactConsultant(false)}
						openByDefault={showContactConsultant}
						captchaSiteKey={props.captchaSiteKey}
						{...contactConsultantInfo}
					/>
				</div>
			)}
			<MyShows
				isOpen={showMyShows}
				onClose={() => setShowMyShows(false)}
				shows={hostParties}
				isLoading={isLoadingMyShows}
			/>
		</>
	);
}
